import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Flag, CheckCircle, XCircle, ExternalLink, RefreshCw,
  ChevronDown, Trash2, ChevronLeft, ChevronRight,
  Calendar, X, Filter, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_API_URL}/api`;

const STATUS_META = {
  pending:   { labelKey: "admin.reports.status.pending",   color: "text-amber-500",        bg: "bg-amber-500/10",      border: "border-amber-500/30"      },
  reviewed:  { labelKey: "admin.reports.status.reviewed",  color: "text-emerald-500",      bg: "bg-emerald-500/10",    border: "border-emerald-500/30"    },
  dismissed: { labelKey: "admin.reports.status.dismissed", color: "text-muted-foreground", bg: "bg-secondary/60",      border: "border-border"            },
};

const REASON_LABELS = {
  misleading: "discover.flagReason.misleading",
  inappropriate: "discover.flagReason.inappropriate",
  duplicate: "discover.flagReason.duplicate",
  hoax: "discover.flagReason.hoax",
  other: "discover.flagReason.other",
};

const STATUS_OPTS = [
  { key: "", labelKey: "admin.reports.filter.allStatus" },
  { key: "pending", labelKey: "admin.reports.status.pending" },
  { key: "reviewed", labelKey: "admin.reports.status.reviewed" },
  { key: "dismissed", labelKey: "admin.reports.status.dismissed" },
];

const REASON_OPTS = [
  { key: "", labelKey: "admin.reports.filter.allReasons" },
  { key: "misleading", labelKey: "discover.flagReason.misleading" },
  { key: "inappropriate", labelKey: "discover.flagReason.inappropriate" },
  { key: "duplicate", labelKey: "discover.flagReason.duplicate" },
  { key: "hoax", labelKey: "discover.flagReason.hoax" },
  { key: "other", labelKey: "discover.flagReason.other" },
];

// ── Simple Select Dropdown ──────────────────────────────────────────────────
function SelectDropdown({ options, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const current = options.find((o) => o.key === value) || options[0];
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        className={`flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-sm border transition-colors ${
          value ? "bg-brand-primary/10 border-brand-primary/30 text-brand-primary" : "border-border bg-card hover:bg-secondary/50"
        }`}
      >
        {current.label} <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 min-w-[160px] bg-card border border-border rounded-sm shadow-lg z-50">
          {options.map((o) => (
            <button
              key={o.key}
              onClick={() => { onChange(o.key); setOpen(false); }}
              className={`block w-full text-left px-3 py-2 text-xs hover:bg-secondary/50 transition-colors ${value === o.key ? "text-brand-primary font-semibold" : ""}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Date Range Picker ───────────────────────────────────────────────────────
function DateRangePicker({ dateFrom, dateTo, onChange, t }) {
  const [open, setOpen] = useState(false);
  const [lf, setLf] = useState(dateFrom || "");
  const [lt, setLt] = useState(dateTo  || "");
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const apply = () => { onChange(lf, lt); setOpen(false); };
  const clear = () => { setLf(""); setLt(""); onChange("", ""); setOpen(false); };
  const hasFilter = dateFrom || dateTo;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="reports-date-range-btn"
        className={`flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-sm border transition-colors ${
          hasFilter ? "bg-brand-primary/10 border-brand-primary/30 text-brand-primary" : "border-border bg-card hover:bg-secondary/50"
        }`}
      >
        <Calendar className="w-3 h-3" />
        {hasFilter ? `${dateFrom || "…"} → ${dateTo || "…"}` : t("discover.dateRange")}
        {hasFilter && <X className="w-3 h-3 ml-0.5" onClick={(e) => { e.stopPropagation(); clear(); }} />}
        {!hasFilter && <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-card border border-border rounded-sm shadow-xl z-50 p-4 space-y-3">
          <p className="text-[10px] font-mono font-semibold uppercase tracking-wider text-muted-foreground">{t("discover.dateRange")}</p>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">{t("discover.dateFrom")}</label>
              <input type="date" value={lf} onChange={(e) => setLf(e.target.value)} data-testid="reports-date-from"
                className="w-full text-xs px-2 py-1.5 rounded-sm border border-border bg-secondary/30 text-foreground focus:outline-none focus:border-brand-primary" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">{t("discover.dateTo")}</label>
              <input type="date" value={lt} onChange={(e) => setLt(e.target.value)} data-testid="reports-date-to"
                className="w-full text-xs px-2 py-1.5 rounded-sm border border-border bg-secondary/30 text-foreground focus:outline-none focus:border-brand-primary" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={apply} className="flex-1 h-7 rounded-sm text-xs bg-brand-primary text-white">{t("discover.applyFilter")}</Button>
            <Button size="sm" variant="ghost" onClick={clear} className="h-7 rounded-sm text-xs">{t("admin.reports.filter.reset")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Confirm Delete Dialog ───────────────────────────────────────────────────
function ConfirmDelete({ count, onConfirm, onCancel, t }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-md p-6 w-80 shadow-2xl space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-verdict-fake/10 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-verdict-fake" />
          </div>
          <div>
            <p className="text-sm font-semibold">{t("admin.reports.deleteConfirm.title")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {count > 1
                ? t("admin.reports.deleteConfirm.multiple", { count })
                : t("admin.reports.deleteConfirm.single")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onCancel} className="flex-1 rounded-sm text-xs h-8">{t("common.cancel")}</Button>
          <Button size="sm" onClick={onConfirm} className="flex-1 rounded-sm text-xs h-8 bg-verdict-fake text-white hover:bg-verdict-fake/90">{t("common.delete")}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────
export default function AdminReports() {
  const { t, i18n } = useTranslation();
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [dateFrom, setDateFrom]         = useState("");
  const [dateTo, setDateTo]             = useState("");

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // null | "single:{id}" | "bulk"

  const statusOptions = STATUS_OPTS.map((option) => ({
    ...option,
    label: t(option.labelKey),
  }));
  const reasonOptions = REASON_OPTS.map((option) => ({
    ...option,
    label: t(option.labelKey),
  }));

  const token = () => localStorage.getItem("auth_token");

  const fetchReports = useCallback(async (p = 1, opts = {}) => {
    setLoading(true);
    try {
      const sf = "status" in opts ? opts.status : statusFilter;
      const rf = "reason" in opts ? opts.reason : reasonFilter;
      const df = "dateFrom" in opts ? opts.dateFrom : dateFrom;
      const dt = "dateTo"   in opts ? opts.dateTo   : dateTo;
      const params = { page: p, limit: 30 };
      if (sf) params.status    = sf;
      if (rf) params.reason    = rf;
      if (df) params.date_from = df;
      if (dt) params.date_to   = dt;
      const { data } = await axios.get(`${API}/admin/content-reports`, {
        params, headers: { Authorization: `Bearer ${token()}` },
      });
      setReports(data.reports || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPages(data.pages || 1);
      setSelected(new Set());
    } catch {
      toast.error(t("admin.reports.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, reasonFilter, dateFrom, dateTo, t]);

  // Intentionally load once here; subsequent reloads are triggered by explicit actions.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReports(1); }, []);

  // ── Single row actions ───────────────────────────────────────────────────
  const updateStatus = async (reportId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/content-reports/${reportId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success(
        newStatus === "reviewed"
          ? t("admin.reports.toast.markedReviewed")
          : t("admin.reports.toast.dismissed"),
      );
      fetchReports(page);
    } catch {
      toast.error(t("admin.reports.toast.updateStatusError"));
    }
  };

  const deleteReport = async (reportId) => {
    try {
      await axios.delete(`${API}/admin/content-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success(t("admin.reports.toast.deleted"));
      fetchReports(page);
    } catch {
      toast.error(t("admin.reports.toast.deleteError"));
    }
    setConfirmDelete(null);
  };

  // ── Bulk actions ─────────────────────────────────────────────────────────
  const bulkAction = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await axios.post(`${API}/admin/content-reports/bulk`, { report_ids: ids, action }, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (action === "delete") toast.success(t("admin.reports.toast.bulkDeleted", { count: ids.length }));
      else toast.success(t("admin.reports.toast.bulkUpdated", { count: ids.length }));
      fetchReports(page);
    } catch {
      toast.error(t("admin.reports.toast.bulkError"));
    }
    setConfirmDelete(null);
  };

  // ── Selection helpers ────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === reports.length) setSelected(new Set());
    else setSelected(new Set(reports.map((r) => r.report_id)));
  };

  const handleDateChange = (df, dt) => {
    setDateFrom(df); setDateTo(dt);
    fetchReports(1, { dateFrom: df, dateTo: dt });
  };

  const activeFilterCount = [statusFilter, reasonFilter, dateFrom || dateTo].filter(Boolean).length;

  return (
    <div className="p-6 md:p-8" data-testid="admin-reports-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Flag className="w-4 h-4 text-brand-primary" />
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("admin.reports.tag")}</span>
          </div>
          <h1 className="font-heading text-2xl font-bold">{t("admin.reports.title")}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t("admin.reports.subtitlePrefix")}{" "}
            <span className="font-semibold text-foreground">{total}</span> {t("admin.reports.subtitleSuffix")}
            {activeFilterCount > 0 && (
              <span className="ml-1 text-brand-primary">({t("admin.reports.filter.active")})</span>
            )}
          </p>
        </div>
        <button
          onClick={() => fetchReports(page)}
          data-testid="reports-refresh-btn"
          className="flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-sm border border-border bg-card hover:bg-secondary/50 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          {t("admin.reports.refresh")}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="reports-filters">
        <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground/60">
          <Filter className="w-3 h-3" /> {t("admin.reports.filter.label")}
        </div>

        <SelectDropdown
          options={statusOptions}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); fetchReports(1, { status: v }); }}
          testId="reports-status-filter"
        />

        <SelectDropdown
          options={reasonOptions}
          value={reasonFilter}
          onChange={(v) => { setReasonFilter(v); fetchReports(1, { reason: v }); }}
          testId="reports-reason-filter"
        />

        <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} t={t} />

        {activeFilterCount > 0 && (
          <button
            onClick={() => {
              setStatusFilter(""); setReasonFilter(""); setDateFrom(""); setDateTo("");
              fetchReports(1, { status: "", reason: "", dateFrom: "", dateTo: "" });
            }}
            className="flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors"
            data-testid="reports-clear-filters"
          >
            <X className="w-3 h-3" /> {t("admin.reports.filter.clear")}
          </button>
        )}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-sm border border-brand-primary/30 bg-brand-primary/5" data-testid="bulk-action-bar">
          <span className="text-xs font-mono text-brand-primary font-semibold">{t("admin.reports.bulk.selected", { count: selected.size })}</span>
          <div className="flex-1" />
          <button
            onClick={() => bulkAction("reviewed")}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors font-mono"
            data-testid="bulk-review-btn"
          >
            <CheckCircle className="w-3 h-3" /> {t("admin.reports.bulk.markReviewed")}
          </button>
          <button
            onClick={() => bulkAction("dismissed")}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-secondary/60 text-muted-foreground border border-border hover:bg-secondary transition-colors font-mono"
            data-testid="bulk-dismiss-btn"
          >
            <XCircle className="w-3 h-3" /> {t("admin.reports.bulk.dismiss")}
          </button>
          <button
            onClick={() => setConfirmDelete("bulk")}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-verdict-fake/10 text-verdict-fake border border-verdict-fake/30 hover:bg-verdict-fake/20 transition-colors font-mono"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-3 h-3" /> {t("common.delete")}
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-muted-foreground hover:text-foreground ml-1">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-5 h-5 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reports.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 border border-dashed border-border rounded-sm text-muted-foreground" data-testid="reports-empty">
          <Flag className="w-8 h-8 mb-3 opacity-30" />
          <p className="text-sm font-mono">
            {statusFilter
              ? t("admin.reports.empty.withStatus", { status: t(`admin.reports.status.${statusFilter}`) })
              : t("admin.reports.empty.default")}
          </p>
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden" data-testid="reports-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-secondary/40 border-b border-border">
                <tr>
                  <th className="px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={selected.size === reports.length && reports.length > 0}
                      onChange={toggleAll}
                      data-testid="select-all-checkbox"
                      className="rounded-sm accent-brand-primary cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-4 py-2.5 font-mono font-semibold text-muted-foreground uppercase tracking-wider">{t("admin.reports.table.analysis")}</th>
                  <th className="text-left px-4 py-2.5 font-mono font-semibold text-muted-foreground uppercase tracking-wider">{t("admin.reports.table.reason")}</th>
                  <th className="text-left px-4 py-2.5 font-mono font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">{t("admin.reports.table.details")}</th>
                  <th className="text-left px-4 py-2.5 font-mono font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">{t("admin.reports.table.reportedAt")}</th>
                  <th className="text-left px-4 py-2.5 font-mono font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">{t("admin.reports.table.type")}</th>
                  <th className="text-left px-4 py-2.5 font-mono font-semibold text-muted-foreground uppercase tracking-wider">{t("admin.reports.table.status")}</th>
                  <th className="text-right px-4 py-2.5 font-mono font-semibold text-muted-foreground uppercase tracking-wider">{t("admin.reports.table.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {reports.map((r, idx) => {
                  const sm = STATUS_META[r.status] || STATUS_META.pending;
                  const rowKey = r.report_id || `row-${idx}`;
                  const isSelected = selected.has(r.report_id);
                  return (
                    <tr
                      key={rowKey}
                      className={`transition-colors ${isSelected ? "bg-brand-primary/5" : "hover:bg-secondary/20"}`}
                      data-testid={`report-row-${r.report_id}`}
                    >
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(r.report_id)}
                          data-testid={`select-${r.report_id}`}
                          className="rounded-sm accent-brand-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={`/share/${r.analysis_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1.5 text-brand-primary hover:underline font-semibold"
                        >
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate max-w-[160px]" title={r.analysis_title}>
                            {r.analysis_title || r.analysis_id}
                          </span>
                        </a>
                      </td>
                      <td className="px-4 py-3 font-mono">
                        {REASON_LABELS[r.reason] ? t(REASON_LABELS[r.reason]) : (r.reason || "—")}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground max-w-[140px] hidden md:table-cell">
                        <span className="truncate block" title={r.details}>{r.details || "—"}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-muted-foreground whitespace-nowrap hidden lg:table-cell">
                        {r.reported_at
                          ? new Date(r.reported_at).toLocaleDateString(i18n.language === "id" ? "id-ID" : "en-US", { day: "numeric", month: "short", year: "numeric" })
                          : "—"}
                      </td>
                      <td className="px-4 py-3 font-mono text-muted-foreground capitalize hidden lg:table-cell">
                        {r.media_type || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono font-semibold border ${sm.color} ${sm.bg} ${sm.border}`}>
                          {t(sm.labelKey)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {r.status !== "reviewed" && (
                            <button
                              onClick={() => updateStatus(r.report_id, "reviewed")}
                              title={t("admin.reports.action.markReviewed")}
                              data-testid={`review-btn-${r.report_id}`}
                              className="p-1.5 rounded-sm text-emerald-500 hover:bg-emerald-500/10 transition-colors"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {r.status !== "dismissed" && (
                            <button
                              onClick={() => updateStatus(r.report_id, "dismissed")}
                              title={t("admin.reports.action.dismiss")}
                              data-testid={`dismiss-btn-${r.report_id}`}
                              className="p-1.5 rounded-sm text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10 transition-colors"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => setConfirmDelete(`single:${r.report_id}`)}
                            title={t("admin.reports.action.delete")}
                            data-testid={`delete-btn-${r.report_id}`}
                            className="p-1.5 rounded-sm text-muted-foreground hover:text-verdict-fake hover:bg-verdict-fake/10 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs font-mono text-muted-foreground">
            {t("admin.reports.pagination.page")} {page} / {pages} — {total} {t("admin.reports.pagination.reports")}
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm"
              onClick={() => fetchReports(page - 1)} disabled={page <= 1}>
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-xs font-mono px-2">{page} / {pages}</span>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm"
              onClick={() => fetchReports(page + 1)} disabled={page >= pages}>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDelete && (
        <ConfirmDelete
          count={confirmDelete === "bulk" ? selected.size : 1}
          t={t}
          onConfirm={() => {
            if (confirmDelete === "bulk") bulkAction("delete");
            else {
              const id = confirmDelete.replace("single:", "");
              deleteReport(id);
            }
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
